/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
export interface UserAlibiSettings {
  settingsGroup: SettingsGroup;
  settings: string;
}

export const SettingsGroups = {
  // special general settings JSON
  GeneralSettings: 'GeneralSettings',
  // sort/filter/search options for wastes, transports, pharmacy, and bin swap
  AnytimeWitness: 'AnytimeWitness',
  AnytimeWitnessFinal: 'AnytimeWitnessFinal',
  AnytimeTransport: 'AnytimeTransport',
  TransportBags: 'TransportBags',
  BlueDiscrepancyBags: 'BlueDiscrepancyBags',
  AssayBags: 'AssayBags',
  ReviewWaste: 'ReviewWaste',
  ReviewTransport: 'ReviewTransport',
  HistoricalWaste: 'HistoricalWaste',
  SentToInvestigationHistoricalWaste: 'SentToInvestigationHistoricalWaste',
  HistoricalTransport: 'HistoricalTransport',
  BinSwaps: 'BinSwaps',
  ReviewBlueCheckBags: "ReviewBlueCheckBags",
  ResolvedBlueCheckBags: "ResolvedBlueCheckBags",
  EscalatedBlueCheckBags: "EscalatedBlueCheckBags",
  HistoricalBlueCheckBags: "HistoricalBlueCheckBags",
  // table column sorts
  AccessTokensTableSort: 'AccessTokensTableSort',
  BarcodesTableSort: 'BarcodesTableSort',
  OrgUsersTableSort: 'OrgUsersTableSort',
  ADMTableSort: 'ADMTableSort',
  OrganizationsTableSort: 'OrganizationsTableSort',
  AuthenticationsTableSort: 'AuthenticationsTableSort',
  VIEWTableSort: 'VIEWTableSort',
  VIEWNotificationsTableSort: 'VIEWNotificationsTableSort',
  RxInterceptRulesTableSort: 'RxInterceptRulesTableSort',
  ServiceListsTableSort: 'ServiceListsTableSort',
  UserGroupUsersTableSort: 'UserGroupUsersTableSort',
  UserGroupsTableSort: 'UserGroupsTableSort',
  UsersTableSort: 'UsersTableSort',
  UserProfileTableSort: 'UserProfileTableSort',
  QueueMessagesTableSort: 'QueueMessagesTableSort',
  FlaggedWastings: 'FlaggedWastings',
  FlaggedByAI: 'FlaggedByAI',
  FlaggedByWitness: 'FlaggedByWitness',
  FlaggedBySystem: 'FlaggedBySystem',
  FlaggedTransports: 'FlaggedTransports',
  TransportFlaggedByAnytimeWitness: 'TransportFlaggedByAnytimeWitness',
  TransportFlaggedBySystem: 'TransportFlaggedBySystem',
  ReadyForDestructionTransport: 'ReadyForDestructionTransport',
  ReadyForDestructionBcb: 'ReadyForDestructionBcb'
} as const;

export const GeneralSettings = {
  // side bar settings
  UiSideBarNavigationExpanded: 'ui-sidenav-expanded',
  UiSideBarReviewRequiredExpanded: 'ui-sidenav-review-required-expanded',
  UiSideBarNewEventsExpanded: 'ui-sidenav-new-events-expanded',
  UiSideBarFlaggedEventsExpanded: 'ui-sidenav-flagged-events-expanded',
  UiSideBarFlaggedWasteEventsExpanded: 'ui-sidenav-flagged-waste-events-expanded',
  UiSideBarFlaggedTransportEventsExpanded: 'ui-sidenav-flagged-transport-events-expanded',
  UiSideBarAwaitingFinalDestructionExpanded: 'ui-sidenav-awaiting-final-destruction-expanded',
  UiSideBarHistoricalEventsExpanded: 'ui-sidenav-historical-events-expanded',
  UiSideBarReportingExpanded: 'ui-sidenav-reporting-expanded',
  UiSideBarAdminExpanded: 'ui-sidenav-admin-expanded',
  UiSideBarSettingsExpanded: 'ui-sidenav-settings-expanded',
  UiSideBarAlibiSystemExpanded: 'ui-sidenav-alibi-system-expanded',
  // theme settings
  UITheme: 'ui-theme',
  // advanced search toggle
  AnytimeWitnessAdvancedSearch: 'anytime-witness-advanced',
  AnytimeWitnessFinalAdvancedSearch: 'anytime-witness-final-advanced',
  AnytimeTransportAdvancedSearch: 'anytime-transport-advanced',
  TransportBagsAdvancedSearch: 'transport-bags-advanced',
  BlueDiscrepancyBagsAdvancedSearch: 'blue-discrepancy-bags-advanced',
  AssayBagsAdvancedSearch: 'assay-bags-advanced',
  ReviewWasteAdvancedSearch: 'review-waste-advanced',
  ReviewTransportAdvancedSearch: 'review-transport-advanced',
  HistoricalWasteAdvancedSearch: 'historical-waste-advanced',
  HistoricalTransportAdvancedSearch: 'historical-transport-advanced',
  BinSwapsAdvancedSearch: 'bin-swaps-advanced',
  HideVideoOverlayHelp: 'hide-video-overlay-help',
  ReviewBlueCheckBagsAdvancedSearch: 'review-blue-check-bags-advanced',
  ResolvedBlueCheckBagsAdvancedSearch: 'resolved-blue-check-bags-advanced',
  EscalatedBlueCheckBagsAdvancedSearch: 'escalated-blue-check-bags-advanced',
  HistoricalBlueCheckBagsAdvancedSearch: 'historical-blue-check-bags-advanced',
  SentToInvestigationHistoricalWasteAdvancedSearch: 'sent-to-investigation-historical-waste-advanced',
  FlaggedWastingsAdvancedSearch: 'flagged-wastings-advanced-search',
  FlaggedByAIAdvancedSearch: 'flagged-by-ai-advanced-search',
  FlaggedByWitnessAdvancedSearch: 'flagged-by-witness-advanced-search',
  FlaggedBySystemAdvancedSearch: 'flagged-by-system-advanced-search',
  FlaggedTransportsAdvancedSearch: 'flagged-transports-advanced-search',
  FlaggedBySystemTransportAdvancedSearch: 'flagged-by-system-transport-advanced-search',
  FlaggedByAnytimeWitnessTransportAdvancedSearch: 'flagged-by-anytime-witness-transport-advanced-search',
  ReadyForDestructionTransportAdvancedSearch: 'ready-for-destruction-transport-advanced-search',
  ReadyForDestructionBcbAdvancedSearch: 'ready-for-destruction-bcb-advanced-search'
} as const;

export type SettingsGroup = (typeof SettingsGroups)[keyof typeof SettingsGroups];

export type GeneralSetting = (typeof GeneralSettings)[keyof typeof GeneralSettings];
